import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import Switch from 'react-switch'
import { fetch_all_permissions } from '../../api/permissionsAPI'
import { create_new_role, delete_role_by_id, fetch_role_by_id, update_role_by_id } from '../../api/roleAPI'
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification'
import { MdAdd } from 'react-icons/md'

export default function RolePermissionFormPage() {
  const navigate = useNavigate()
  const role_id = new URLSearchParams(window.location.search).get("role_id")
  const [permissionsData, setPermissionsData] = useState([])
  const [searchQuery, setSearchQuery] = useState("")

  const [roleData, setRoleData] = useState({
    name: '',
    permissions: [],
    is_super_admin: '0',
    description: '',
    redirect: "/home"
  })

  const fetchPermissions = async () => {
    let { data, error, message } = await fetch_all_permissions()
    if (error) return console.error(message)
    setPermissionsData(data)
  }


  const fetchRoleById = async () => {
    if (!role_id || role_id === 'new') {
      return; // No role ID provided, skip fetching
    }
    let { data, message, error } = await fetch_role_by_id(role_id)
    if (error) return console.error(error)
    setRoleData(data)
  }

  useEffect(() => {
    fetchRoleById()
    fetchPermissions()
  }, [role_id])


  const createNewRole = async () => {
    if (role_id !== 'new') return
    let { data, message, error } = await create_new_role(roleData)
    if (error) return notify({ message, type: "error" })
    navigate(`/admin/roles-permissions/details?role_id=${data.id}`)
    notify({ message: "Role created successfully", type: "success" })
  }

  const updateRoleById = async () => {
    if (role_id === 'new') return
    let { data, message, error } = await update_role_by_id(role_id, roleData)
    if (error) return notify({ message, type: "error" })
    fetchRoleById()
    notify({ message: "Role updated successfully", type: "success" })
  }

  const deleteRoleById = async () => {
    if (role_id === 'new') return
    let { data, message, error } = await delete_role_by_id(role_id)
    if (error) return notify({ message, type: "error" })
    notify({ message: "Role deleted successfully", type: "success" })
    navigate(-1)
  }

  return (
    <AdminLayout
      navSelected={"Roles & Permissions"}
      headerText={"Role & Permissions"}
      toolbar={
        <>
          <h1 className="text-2xl font-bold text-gray-900">
            {role_id === "new" ? "Create Role" : "Edit Role"}
          </h1>
          <div className="flex gap-2">
            {role_id !== "new" && (
              <button 
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                onClick={deleteRoleById}
              >
                Delete
              </button>
            )}
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              onClick={() => {
                if (role_id === "new") {
                  createNewRole()
                } else {
                  updateRoleById()
                }
              }}
            >
              {role_id === "new" ? "Create" : "Save"}
            </button>
          </div>
        </>
      }
    >
      <div className="flex flex-row items-start gap-2">
        <div className="w-full">
          <div className="card mb-2">
            <p className="card-title">{role_id && role_id !== "new" ? "Edit Role" : "Create Role"}</p>
            <div className="input-container">
              <label htmlFor="" className="input-label">Role</label>
              <input
                type="text"
                className="text-input"
                placeholder="Enter Role name"
                value={roleData.name}
                onChange={(e) => setRoleData({ ...roleData, name: e.target.value })}
              />
            </div>
            <div className="input-container">
              <label htmlFor="" className="input-label">Redirect URL</label>
              <input
                type="text"
                className="text-input"
                placeholder="Enter Redirect URL"
                value={roleData.redirect}
                onChange={(e) => setRoleData({ ...roleData, redirect: e.target.value })}
              />
            </div>
            <div className="input-container">
              <label htmlFor="" className="input-label">Super Admin</label>
              <Switch
                checked={roleData.is_super_admin == '1'}
                onChange={(checked) => setRoleData({ ...roleData, is_super_admin: checked ? '1' : '0' })}
              />
            </div>
            <div className="input-container">
              <label htmlFor="" className="input-label">Description</label>
              <textarea
                className="textarea-input"
                placeholder="A brief description of the User Role"
                value={roleData.description}
                onChange={(e) => setRoleData({ ...roleData, description: e.target.value })}
              />
            </div>
          </div>
          <div className="card">
            <p className="card-title">Permissions</p>
            {/* Search Bar */}
            <div>
              <input
                type="text"
                className="text-input mb-4"
                placeholder="Search Permissions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              {Object.keys(permissionsData).map((category) => {
                if (searchQuery && !permissionsData[category]?.[Object.keys(permissionsData[category])?.[0]]?.name?.toLowerCase().includes(searchQuery?.toLowerCase())) {
                  return null; // Skip this category if it doesn't match the search query
                }
                return (
                  <div key={category} className='mb-2'>
                    <div className="flex flex-row items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">{permissionsData[category][Object.keys(permissionsData[category])[0]]?.category}</h3>
                      {/* Select All */}
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          const categoryPermissions = Object.values(permissionsData[category]).map(p => p.key)
                          const allSelected = categoryPermissions.every(key => roleData.permissions.includes(key))
                          let updatedPermissions
                          if (allSelected) {
                            // Deselect all in this category
                            updatedPermissions = roleData.permissions.filter(p => !categoryPermissions.includes(p))
                          } else {
                            // Add all in this category
                            updatedPermissions = Array.from(new Set([...roleData.permissions, ...categoryPermissions]))
                          }
                          setRoleData({ ...roleData, permissions: updatedPermissions })
                        }}
                      >
                        {(Object.values(permissionsData[category]).every(p => roleData.permissions.includes(p.key)) || roleData?.is_super_admin == 1) ? 'Unselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="flex-col flex gap-2">
                      {Object.keys(permissionsData[category]).map((permissionKey) => {
                        if (searchQuery && !permissionsData[category][permissionKey].name.toLowerCase().includes(searchQuery.toLowerCase())) {
                          return null; // Skip this permission if it doesn't match the search query
                        }
                        const permission = permissionsData[category][permissionKey]
                        return (
                          <div key={permission.key} className="flex items-center gap-2 justify-between">
                            <div className='flex flex-col'>
                              <label htmlFor="" className="text-sm font-bold">{permission.name}</label>
                              <label htmlFor="" className="input-label text-gray-500">{permission.description}</label>
                            </div>
                            <Switch
                              checked={roleData.permissions.includes(permission.key) || roleData?.is_super_admin == 1}
                              onChange={(checked) => {
                                let updatedPermissions = [...roleData.permissions]
                                if (checked) {
                                  updatedPermissions.push(permission.key)
                                } else {
                                  updatedPermissions = updatedPermissions.filter(p => p !== permission.key)
                                }
                                setRoleData({ ...roleData, permissions: updatedPermissions })
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {/* <pre>{JSON.stringify(permissionsData, null, 2)}</pre> */}
        </div>
      </div>
    </AdminLayout >
  )
}
